import { memo, useCallback } from 'react';
import {
  NodeToolbar,
  useEdges,
  useReactFlow,
  useStore,
} from 'reactflow';
import GuardedNodeResizer from '../ReactFlow/GuardedNodeResizer';

import { Box, Paper } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useInputSizeTracking } from '../../hooks/useInputSizeTracking';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { NodeProps } from '../../util/Types/NodeProps';
import { useNodeFieldAutoSave } from '../../util/autosave/useNodeFieldAutoSave';
import { useDetachNodes } from '../../util/useDetachNodes';
import DeleteNode from '../UI/DeleteNode/DeleteNode';

function TextBoxNode({ id, data }: NodeProps) {
  const { setNodes: setNodeState } = useReactFlow();
  const { user: authUser } = useAuthStore(state => state);

  const { setSaving, setSchema } = useFlowsStore(state => state);
  const detachNodes = useDetachNodes();
  const hasParent = useStore(
    store => !!store.nodeInternals.get(id)?.parentNode,
  );
  const edges = useEdges();
  const { flowId } = useParams();

  const inputSize = useInputSizeTracking({
    nodeId: id,
    inputSize: data?.inputSize,
    collapsed: data?.collapsed,
    edges,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
  });

  const onDetach = () => detachNodes([id]);

  const [textBoxValue, setTextBoxValue] = useNodeFieldAutoSave<string>({
    externalValue: data?.text ?? '',
    fieldName: 'text',
    nodeId: id,
    flowId,
    edges,
    setNodeState,
    setSaving,
    setSchema,
    debounceMs: 500,
  });

  const onChange = useCallback(
    (evt: React.ChangeEvent<HTMLTextAreaElement>) =>
      setTextBoxValue(evt.target.value),
    [setTextBoxValue],
  );

  return (
    <>
      {authUser?.theme === 'dark' && (
        <Box
          sx={{
            backgroundColor: authUser?.theme !== 'dark' ? '#FDDF82' : '#FDDF82', // yellow
            height: '10px',
            borderRadius: '4px 4px 0px 0px',
          }}
        />
      )}
      <Paper
        sx={{
          padding: theme =>
            authUser?.theme !== 'dark' ? theme.spacing(2) : '0 16px 16px 16px',
          backgroundColor: authUser?.theme !== 'dark' ? ' #FDDF82' : '#1D2939', // yellow
          borderRadius: authUser?.theme !== 'dark' ? '4px' : '0 0 4px 4px', // no curves
          position: 'relative',
          boxShadow:
            '0px 8px 38px 7px rgba(0, 0, 0, 0.12), 0px 20px 31px 3px rgba(0, 0, 0, 0.14), 0px 10px 13px -6px rgba(0, 0, 0, 0.20)',
        }}
      >
        <GuardedNodeResizer isVisible={false} />

        {/* <br /> */}
        <NodeToolbar className="nodrag">
          {hasParent && <button onClick={onDetach}>Detach</button>}
        </NodeToolbar>
        <Box display={'flex'} justifyContent={'end'}>
          <DeleteNode nodeId={id} />
        </Box>
        <Box>
          {!data.collapsed && (
            <>
              <textarea
                placeholder={'Add any comments you would like'}
                disabled={data?.disabled}
                value={textBoxValue}
                onChange={onChange}
                name="text"
                className="nodrag nowheel"
                maxLength={200}
                style={{
                  resize: 'both',
                  color: authUser?.theme !== 'dark' ? '#344054' : '#D0D5DD',
                  overflow: 'hidden',
                  width: inputSize?.width || '252px',
                  textAlign: 'center',
                  fontFamily: 'inherit',
                  fontSize: '20px',
                  height: inputSize?.height || '220px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                }}
              ></textarea>
            </>
          )}
        </Box>
      </Paper>
    </>
  );
}

export default memo(TextBoxNode);
